/**
 * Estimated Delivery Date Web Component
 *
 * Takes a pincode on the product page and asks the klickpost worker
 * (GET /edd) when that order would arrive. The worker owns the Delhivery
 * credential and the date arithmetic; this component only collects input,
 * renders the answer, and remembers the pincode for next time.
 *
 * Failure posture: every unhappy path leaves the buy button usable and says so
 * plainly. A delivery estimate is a nice-to-have -- it must never read as if
 * the order itself is blocked.
 *
 * EVERYTHING lives inside the IIFE below, and that is load-bearing rather than
 * stylistic. Theme assets are classic scripts, so they all share one global
 * lexical scope. This file previously declared `const STORAGE_KEY` at top
 * level, which dispatch-timer.js also does ('kreo:dispatch-timer'); both render
 * on every product page, so whichever parsed second died with
 *   SyntaxError: Identifier 'STORAGE_KEY' has already been declared
 * A parse error aborts the whole file, so customElements.define() never ran and
 * the widget rendered perfectly while doing nothing at all. Keep the wrapper,
 * and keep new bindings inside it.
 *
 * Guarded by .verify/check-edd-checker-scope.mjs.
 */
(function () {
  'use strict';

  const STORAGE_KEY = 'kreo:edd-pincode';
  /** Indian PINs are six digits and never start with zero. Mirrors isValidPincode in the worker. */
  const PIN_RE = /^[1-9]\d{5}$/;

  // Deliberately reassuring rather than alarming: a delivery estimate is a
  // nice-to-have, and a shopper must never read a failure here as the order
  // itself being blocked.
  const UNAVAILABLE_LEDE = "Couldn't check just now";
  const UNAVAILABLE_META = "we'll confirm at checkout";

  class EddChecker extends HTMLElement {
    connectedCallback() {
      this._endpoint = (this.dataset.endpoint || '').replace(/\/+$/, '');
      this._input = this.querySelector('.edd-checker__input');
      this._button = this.querySelector('.edd-checker__submit');
      this._out = this.querySelector('.edd-checker__out');
      this._lede = this.querySelector('.edd-checker__lede');
      this._meta = this.querySelector('.edd-checker__meta');
      if (!this._input || !this._button || !this._out || !this._lede || !this._meta) return;

      this._onClick = () => this._check();
      this._onKeydown = (e) => {
        if (e.key !== 'Enter') return;
        // The buy button lives in a form; letting Enter bubble would submit it.
        e.preventDefault();
        this._check();
      };
      this._onInput = () => {
        const cleaned = this._input.value.replace(/\D/g, '').slice(0, 6);
        if (cleaned !== this._input.value) this._input.value = cleaned;
      };

      this._button.addEventListener('click', this._onClick);
      this._input.addEventListener('keydown', this._onKeydown);
      this._input.addEventListener('input', this._onInput);

      const saved = this._readSaved();
      if (saved) {
        this._input.value = saved;
        this._check();
      }
    }

    disconnectedCallback() {
      if (this._button) this._button.removeEventListener('click', this._onClick);
      if (this._input) {
        this._input.removeEventListener('keydown', this._onKeydown);
        this._input.removeEventListener('input', this._onInput);
      }
      if (this._controller) this._controller.abort();
    }

    _readSaved() {
      try {
        const v = localStorage.getItem(STORAGE_KEY);
        return v && PIN_RE.test(v) ? v : null;
      } catch (e) {
        return null; // localStorage unavailable (private mode, blocked cookies)
      }
    }

    _save(pin) {
      try {
        localStorage.setItem(STORAGE_KEY, pin);
      } catch (e) { /* localStorage unavailable */ }
    }

    _setBusy(busy) {
      this._button.disabled = busy;
      this._button.textContent = busy ? 'Checking…' : 'Check';
    }

    /**
     * Renders a result: a lead-in, an optional emphasised value (the date, or
     * the pincode we cannot reach), and a supporting caption.
     *
     * Text is assigned via textContent/replaceChildren rather than innerHTML,
     * so nothing the worker returns can inject markup. The badge glyph is
     * supplied by CSS per data-state, so there is no icon markup to sanitise.
     */
    _render(state, lede, strongText, meta) {
      this._out.dataset.state = state;

      if (strongText) {
        const strong = document.createElement('strong');
        strong.textContent = strongText;
        this._lede.replaceChildren(document.createTextNode(lede), strong);
      } else {
        this._lede.textContent = lede;
      }
      this._meta.textContent = meta || '';

      // Retrigger the entry animation on each new answer.
      this._out.removeAttribute('data-animate');
      void this._out.offsetWidth;
      this._out.setAttribute('data-animate', '');
    }

    async _check() {
      const pin = this._input.value.trim();
      if (!PIN_RE.test(pin)) {
        this._render('error', 'Enter a valid 6-digit pincode', null, '');
        return;
      }

      if (this._controller) this._controller.abort();
      this._controller = new AbortController();
      const signal = this._controller.signal;

      this._setBusy(true);
      try {
        const res = await fetch(`${this._endpoint}/edd?pincode=${encodeURIComponent(pin)}`, { signal });

        if (res.status === 429) {
          this._render('error', 'Too many checks just now', null, 'try again in a minute');
          return;
        }
        if (!res.ok) {
          this._render('error', UNAVAILABLE_LEDE, null, UNAVAILABLE_META);
          return;
        }

        const data = await res.json();
        this._save(pin);

        if (data && data.serviceable && data.deliveryLabel) {
          this._render('ok', 'Get it by ', data.deliveryLabel, `to ${pin}`);
        } else if (data && data.serviceable === false) {
          this._render('unserviceable', "We don't deliver to ", pin, 'try another pincode');
        } else {
          this._render('error', UNAVAILABLE_LEDE, null, UNAVAILABLE_META);
        }
      } catch (e) {
        if (signal.aborted) return; // superseded by a newer check, or component removed
        this._render('error', UNAVAILABLE_LEDE, null, UNAVAILABLE_META);
      } finally {
        if (!signal.aborted) this._setBusy(false);
      }
    }
  }

  customElements.define('edd-checker', EddChecker);
})();
